import type { EndEvent, RTCSession } from 'jssip/lib/RTCSession';

import { LogLevel } from 'features/analytics/enums';
import {
  sendAnalyticsInfo,
  sendAnalyticsWarning,
} from 'features/analytics/helpers';
import { sendAnalytics } from 'features/analytics/store';
import { RtcSessionTracker } from 'features/sip/services';
import { getConnectionChangeTypeByOriginator } from 'features/sip/utils';

// Define the dispatch type based on the createAsyncThunk dispatch
type AsyncThunkDispatch = (action: any) => any;

/**
 * Analytics helper for session tracking events
 */
export const sessionTrackingAnalytics = {
  /**
   * Log when a regular session is accepted and tracked as original
   */
  originalSessionAccepted: (
    dispatch: AsyncThunkDispatch,
    sessionRemoteUri: string,
    session: RTCSession
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.originalSessionAccepted',
        Message: 'Original session accepted and tracked for potential REFER',
        SessionRemoteUri: sessionRemoteUri,
        SessionDirection: session.direction,
        SessionId: session.id,
      })
    );
  },

  /**
   * Log when a teaming session is accepted
   */
  teamingSessionAccepted: (
    dispatch: AsyncThunkDispatch,
    sessionRemoteUri: string,
    session: RTCSession,
    originalSessionUri?: string
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.teamingSessionAccepted',
        Message:
          'Teaming session accepted, configured refer to use original session',
        TeamingSessionUri: sessionRemoteUri,
        TeamingSessionDirection: session.direction,
        TeamingSessionId: session.id,
        OriginalSessionUri: originalSessionUri,
      })
    );
  },

  /**
   * Log when session references are updated
   */
  sessionReferencesUpdated: (
    dispatch: AsyncThunkDispatch,
    operation: string,
    details: Record<string, any>
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.sessionReferencesUpdated',
        Message: `Session references updated: ${operation}`,
        ...details,
      })
    );
  },

  /**
   * Log when all session references are cleaned up
   */
  allSessionsCleanedUp: (dispatch: AsyncThunkDispatch, reason: string) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.allSessionsCleanedUp',
        Message: 'All session references cleared and ensured are disconnected',
        Reason: reason,
      })
    );
  },

  /**
   * Log when refer session ref is updated
   */
  referSessionUpdated: (
    dispatch: AsyncThunkDispatch,
    newSessionUri: string | null,
    reason: string
  ) => {
    dispatch(
      sendAnalyticsInfo({
        Method: 'sessionTrackingAnalytics.referSessionUpdated',
        Message: 'Refer session reference updated',
        NewReferSessionUri: newSessionUri,
        Reason: reason,
      })
    );
  },
};

/**
 * Analytics helper for session end events
 */
export const sessionEndAnalytics = {
  /**
   * Log when ignoring BYE messages during teaming
   * records the TeamingType (e.g., RemoteRq, RemoteIc, Request, AdhocReq)
   */
  ignoringByeMessage: (
    dispatch: AsyncThunkDispatch,
    teamingType: string | undefined
  ) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk',
        Message:
          'Ignoring BYE message not from svrs-rt endpoint during teaming',
        Level: LogLevel.INFO,
        TeamingType: teamingType,
      })
    );
  },

  /**
   * Log basic session end details
   */
  sessionEnded: (dispatch: AsyncThunkDispatch, event: EndEvent) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEnded',
        Originator: event.originator,
        Cause: event.cause,
        Message: `Session ended with cause: ${event.cause}`,
        Level: LogLevel.INFO,
      })
    );
  },

  /**
   * Log when completing a teaming call from the teaming flow
   */
  completingTeamingCall: (
    dispatch: AsyncThunkDispatch,
    params: {
      teamingStatus: string | number;
      acceptingUserId?: string | number;
      callId?: string | number;
    }
  ) => {
    const { teamingStatus, acceptingUserId, callId } = params;
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.completingTeamingCall',
        Level: LogLevel.INFO,
        Message: 'Completing teaming call',
        TeamingStatus: teamingStatus,
        AcceptingUserId: acceptingUserId,
        CallId: callId,
      })
    );
  },

  /**
   * Log when canceling a teaming request
   */
  cancelingTeamingRequest: (
    dispatch: AsyncThunkDispatch,
    params: {
      teamingStatus: string | number;
      acceptingUserId?: string | number;
      callId?: string | number;
    }
  ) => {
    const { teamingStatus, acceptingUserId, callId } = params;
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.cancelingTeamingRequest',
        Level: LogLevel.INFO,
        Message: 'Canceling teaming request',
        TeamingStatus: teamingStatus,
        AcceptingUserId: acceptingUserId,
        CallId: callId,
      })
    );
  },

  /**
   * Log emitting deaf disconnect event
   */
  emitDeafDisconnect: (
    dispatch: AsyncThunkDispatch,
    event: EndEvent,
    isSpecialEndEvent: boolean
  ) => {
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.emitDeafDisconnect',
        Level: LogLevel.INFO,
        Message: 'Emitting deaf disconnect event',
        ConnectionChangeType: getConnectionChangeTypeByOriginator(
          event.originator
        ),
        IsConnectionCallStateNeeded: !isSpecialEndEvent,
      })
    );
  },

  /**
   * Log skipping deaf disconnect
   */
  skipDeafDisconnect: (
    dispatch: AsyncThunkDispatch,
    params: {
      isOriginalReferSession: boolean;
      isRefer: boolean;
      isTeaming: boolean;
      shouldSkipDeafDisconnect: boolean;
    }
  ) => {
    const {
      isOriginalReferSession,
      isRefer,
      isTeaming,
      shouldSkipDeafDisconnect,
    } = params;
    dispatch(
      sendAnalytics({
        Method: 'handleRtcSessionEndedThunk.skipDeafDisconnect',
        Level: LogLevel.INFO,
        Message:
          'Skipping deaf disconnect for original REFER session during teaming',
        IsOriginalReferSession: isOriginalReferSession,
        IsRefer: isRefer,
        IsTeaming: isTeaming,
        ShouldSkipDeafDisconnect: shouldSkipDeafDisconnect,
      })
    );
  },
};

/**
 * Centralized analytics for session accepted events (teaming/original)
 */
export function sendSessionAcceptedAnalytics(
  dispatch: AsyncThunkDispatch,
  sessionRole: string | undefined,
  currentSession: RTCSession
) {
  const sessionRemoteUri =
    currentSession.remote_identity?.uri?.toString() || '';

  if (sessionRole === 'teaming') {
    sessionTrackingAnalytics.teamingSessionAccepted(
      dispatch,
      sessionRemoteUri,
      currentSession,
      RtcSessionTracker.getDirectSession()?.remote_identity?.uri?.toString()
    );
  } else if (sessionRole === 'direct') {
    sessionTrackingAnalytics.originalSessionAccepted(
      dispatch,
      sessionRemoteUri,
      currentSession
    );
  } else {
    // Fallback: unknown session role
    dispatch(
      sendAnalyticsWarning({
        Method: 'sessionTrackingAnalytics.unknownSessionRole',
        Message: 'Session accepted with unknown role',
        SessionRemoteUri: sessionRemoteUri,
        SessionId: currentSession.id,
      })
    );
  }
}

/**
 * Centralized analytics for session disconnection events
 */
export function sendSessionDisconnectedAnalytics(
  dispatch: AsyncThunkDispatch,
  sessionRole: 'teaming' | 'direct' | 'wavello' | undefined,
  currentSession: RTCSession
) {
  const sessionRemoteUri =
    currentSession.remote_identity?.uri?.toString() || '';

  const sessionDetails = {
    SessionRemoteUri: sessionRemoteUri,
    SessionDirection: currentSession.direction,
    SessionId: currentSession.id,
    ConnectionState: currentSession.connection?.connectionState,
  };

  switch (sessionRole) {
    case 'teaming':
      dispatch(
        sendAnalyticsInfo({
          Method: 'sessionTrackingAnalytics.teamingSessionDisconnected',
          Message: 'Teaming session guaranteed disconnected and cleaned up',
          ...sessionDetails,
        })
      );
      return;
    case 'direct':
      dispatch(
        sendAnalyticsInfo({
          Method: 'sessionTrackingAnalytics.directSessionDisconnected',
          Message: 'Direct session guaranteed disconnected and cleaned up',
          ...sessionDetails,
        })
      );
      return;
    case 'wavello':
      dispatch(
        sendAnalyticsInfo({
          Method: 'sessionTrackingAnalytics.wavelloSessionDisconnected',
          Message: 'Wavello session guaranteed disconnected and cleaned up',
          ...sessionDetails,
        })
      );
      return;
    default: {
      const _exhaustive: undefined = sessionRole;
      void _exhaustive;
      dispatch(
        sendAnalyticsWarning({
          Method: 'sessionTrackingAnalytics.unknownSessionRole',
          Message: 'Session disconnected with unknown role',
          ...sessionDetails,
        })
      );
    }
  }
}

/**
 * Analytics for when we force close a session that wasn't properly disconnected
 */
export function sendSessionForceClosedAnalytics({
  dispatch,
  sessionRole,
  oldSession,
  newSession,
}: {
  dispatch: AsyncThunkDispatch;
  sessionRole: string;
  oldSession: RTCSession;
  newSession: RTCSession;
}) {
  const oldSessionUri = oldSession.remote_identity?.uri?.toString() || '';
  const newSessionUri = newSession.remote_identity?.uri?.toString() || '';

  dispatch(
    sendAnalyticsWarning({
      Method: 'sessionTrackingAnalytics.sessionForceClosed',
      Message: `Forced disconnection of ${sessionRole} session that was not properly closed during session replacement`,
      SessionRole: sessionRole,
      OldSessionUri: oldSessionUri,
      OldSessionId: oldSession.id,
      OldConnectionState: oldSession.connection?.connectionState,
      OldSignalingState: oldSession.connection?.signalingState,
      NewSessionUri: newSessionUri,
      NewSessionId: newSession.id,
      Reason: 'Session replacement detected incompletely disconnected session',
    })
  );
}

/**
 * Track analytics when all RTC sessions are disconnected and cleared
 */
export function trackAllSessionsDisconnectedAnalytics(
  dispatch: AsyncThunkDispatch,
  directSessionUri: string,
  teamingSessionUri: string,
  nonActiveSessionCount: number
) {
  dispatch(
    sendAnalytics({
      Method: 'RtcSessionTracker.disconnectAndClearAll',
      Level: LogLevel.INFO,
      Message: 'All RTC sessions disconnected and cleared',
      DirectSessionUri: directSessionUri || 'none',
      TeamingSessionUri: teamingSessionUri || 'none',
      NonActiveSessionCount: nonActiveSessionCount,
    })
  );
}
